import { apiClient } from './client';
import { appConfig } from '@/config/app-config';

/**
 * バックエンドの共通レスポンス型
 */
interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
}

/**
 * 既存Sugino APIのユーザー型（Cognito UserType互換）
 */
export interface CognitoUserAttribute {
  Name: string;
  Value: string;
}

export interface CognitoUserRaw {
  Username: string;
  Attributes?: CognitoUserAttribute[];
  UserStatus?: string;
  Enabled?: boolean;
  isAdministrator?: boolean;
}

/**
 * フロントエンド用の簡略化されたユーザー型
 */
export interface CognitoUser {
  username: string;
  email: string;
  enabled: boolean;
  status: string;
  isAdministrator?: boolean;
}

/**
 * バックエンドから返されるユーザー一覧レスポンス型
 */
interface ListUsersApiResponse {
  Users?: CognitoUserRaw[];
  PaginationToken?: string;
}

interface ListUsersInGroupApiResponse {
  Users?: CognitoUserRaw[];
  NextToken?: string;
}

export interface CreateUserResponse {
  username: string;
  email: string;
}

/**
 * CognitoUserRawからCognitoUserに変換
 */
function convertUser(raw: CognitoUserRaw): CognitoUser {
  const email = raw.Attributes?.find(attr => attr.Name === 'email')?.Value || raw.Username;
  return {
    username: raw.Username,
    email,
    enabled: raw.Enabled ?? true,
    status: raw.UserStatus || 'UNKNOWN',
    isAdministrator: raw.isAdministrator,
  };
}

/**
 * 全ユーザー一覧取得
 * GET /users
 */
export async function listUsers(): Promise<{ users: CognitoUser[] }> {
  let allUsers: CognitoUserRaw[] = [];
  let paginationToken = '';

  do {
    const params = new URLSearchParams();
    if (paginationToken) {
      params.append('paginationToken', paginationToken);
    }

    const url = paginationToken ? `/users?${params.toString()}` : '/users';
    const response = await apiClient.get<ApiResponse<ListUsersApiResponse>>(url);
    const payload = response.data.data;

    if (payload.Users) {
      allUsers = allUsers.concat(payload.Users);
    }
    paginationToken = payload.PaginationToken || '';
  } while (paginationToken);

  return { users: allUsers.map(convertUser) };
}

/**
 * グループ選択肢型
 * value: Cognitoグループ名（内部ID）、label: 日本語表示名
 */
export interface GroupOption {
  value: string;
  label: string;
}

/**
 * グループ一覧取得
 * 設定ファイルから取得（administrators を先頭に追加）
 * value にはCognitoグループ名、label には日本語表示名を返す。
 */
export async function listGroups(): Promise<{ groups: GroupOption[] }> {
  const groups: GroupOption[] = appConfig.groupFolderMapping
    .filter(m => Boolean(m.group))
    .map(m => ({
      value: m.group,
      label: m.label,
    }));
  if (!groups.some(g => g.value === 'administrators')) {
    groups.unshift({ value: 'administrators', label: '管理者' });
  }
  return { groups };
}

/**
 * グループ内ユーザー取得
 * GET /groups/{groupName}
 */
export async function listUsersInGroup(groupName: string): Promise<{ users: CognitoUser[] }> {
  const response = await apiClient.get<ApiResponse<ListUsersInGroupApiResponse>>(
    `/groups/${encodeURIComponent(groupName)}`
  );

  const users = (response.data.data.Users || []).map(convertUser);
  return { users };
}

/**
 * ユーザーをグループに追加
 * POST /users/{username}/groups/{groupName}
 */
export async function addUserToGroup(username: string, groupName: string): Promise<void> {
  await apiClient.post(
    `/users/${encodeURIComponent(username)}/groups/${encodeURIComponent(groupName)}`,
    {}
  );
}

/**
 * ユーザーをグループから削除
 * DELETE /users/{username}/groups/{groupName}
 */
export async function removeUserFromGroup(username: string, groupName: string): Promise<void> {
  await apiClient.delete(
    `/users/${encodeURIComponent(username)}/groups/${encodeURIComponent(groupName)}`
  );
}

export interface UpdateGroupMembersRequest {
  groupName: string;
  addUsers?: string[];
  removeUsers?: string[];
}

export interface UpdateGroupMembersResponse {
  groupName: string;
  results: {
    added: { success: string[]; failed: string[] };
    removed: { success: string[]; failed: string[] };
  };
}

/**
 * ユーザー新規作成
 * POST /users
 */
export async function createUser(email: string): Promise<CreateUserResponse> {
  const response = await apiClient.post<ApiResponse<CreateUserResponse>>(
    '/users',
    { email }
  );
  return response.data.data;
}

/**
 * ユーザー無効化
 * POST /users/{username}/disable
 */
export async function disableUser(username: string): Promise<void> {
  await apiClient.post(`/users/${encodeURIComponent(username)}/disable`, {});
}

/**
 * ユーザー有効化
 * POST /users/{username}/enable
 */
export async function enableUser(username: string): Promise<void> {
  await apiClient.post(`/users/${encodeURIComponent(username)}/enable`, {});
}

/**
 * 複数ユーザーをグループに追加/削除（バッチ処理）
 */
export async function updateGroupMembers(
  request: UpdateGroupMembersRequest
): Promise<{ success: boolean; data?: UpdateGroupMembersResponse; error?: string }> {
  const results: UpdateGroupMembersResponse = {
    groupName: request.groupName,
    results: {
      added: { success: [], failed: [] },
      removed: { success: [], failed: [] },
    },
  };

  if (request.addUsers) {
    for (const username of request.addUsers) {
      try {
        await addUserToGroup(username, request.groupName);
        results.results.added.success.push(username);
      } catch (error) {
        console.error(`Failed to add ${username} to ${request.groupName}:`, error);
        results.results.added.failed.push(username);
      }
    }
  }

  if (request.removeUsers) {
    for (const username of request.removeUsers) {
      try {
        await removeUserFromGroup(username, request.groupName);
        results.results.removed.success.push(username);
      } catch (error) {
        console.error(`Failed to remove ${username} from ${request.groupName}:`, error);
        results.results.removed.failed.push(username);
      }
    }
  }

  const hasFailures =
    results.results.added.failed.length > 0 ||
    results.results.removed.failed.length > 0;

  return {
    success: !hasFailures,
    data: results,
    error: hasFailures ? '一部の操作に失敗しました' : undefined,
  };
}
